import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { Header } from "./components/Header";
import { CookieBanner } from "./components/CookieBanner";
import { WhatsAppButton } from "./components/WhatsAppButton";
import { InstallPWA } from "./components/InstallPWA";
import SupportChatWidget from "./components/SupportChatWidget";
import { ScrollToTop } from "./components/ScrollToTop";
import { Suspense, lazy } from "react";
import { useAuth } from "./context/AuthContext";
import "./App.css";

// Lazy loading - las paginas se cargan solo cuando se visitan
const HomePage = lazy(() => import("./pages/HomePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const StreamPage = lazy(() => import("./pages/StreamPage"));
const GoLivePage = lazy(() => import("./pages/GoLivePage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ShortLinkRedirect = lazy(() => import("./pages/ShortLinkRedirect"));
const ProPage = lazy(() => import("./pages/ProPage"));
const RankingPage = lazy(() => import("./pages/RankingPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const StreamersPage = lazy(() => import("./pages/StreamersPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ConfigGuide = lazy(() => import("./pages/ConfigGuide"));
const RestreamSettings = lazy(() => import("./pages/RestreamSettings"));
const MatchCalendarPage = lazy(() => import("./pages/MatchCalendarPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const MarsuCoinsPage = lazy(() => import("./pages/MarsuCoinsPage"));
const AdsPage = lazy(() => import("./pages/AdsPage"));
const DownloadPage = lazy(() => import("./pages/DownloadPage"));

// Redirect logged-in users away from auth pages
const GuestRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center p-8 rounded-2xl bg-white/5 border border-white/10 max-w-md mx-4">
          <div className="w-16 h-16 rounded-full bg-[#7C3AED]/20 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-[#7C3AED]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Ya tienes sesión iniciada</h2>
          <p className="text-gray-400 mb-6">Estás conectado como <span className="text-[#7C3AED] font-semibold">@{user.username}</span></p>
          <div className="flex flex-col gap-3">
            <a href="/" className="px-6 py-2.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl font-medium transition-colors" data-testid="go-home-btn">Ir al inicio</a>
            <a href={`/profile/${encodeURIComponent(user.username)}`} className="px-6 py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-xl font-medium transition-colors" data-testid="go-profile-btn">Ver mi perfil</a>
          </div>
        </div>
      </div>
    );
  }
  
  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Streamer Only Route Component
const StreamerRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.user_type !== 'streamer') {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  // Detectar modo TV: User-Agent (WebView) + localStorage (set by tv-landing) + URL param
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('tv') === 'firetv' || navigator.userAgent.includes('TuLigaTV')) {
    try { localStorage.setItem('tuliga_tv_mode', 'firetv'); } catch(e) {}
  }
  const isTvApp = navigator.userAgent.includes('TuLigaTV') || localStorage.getItem('tuliga_tv_mode') === 'firetv';

  return (
    <div className="app-container">
      <ScrollToTop />
      <div className="noise-overlay"></div>
      {!isTvApp && <Header />}
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="spinner"></div>
        </div>
      }>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/stream/:streamId" element={<StreamPage />} />
        <Route 
          path="/go-live" 
          element={
            <StreamerRoute>
              <GoLivePage />
            </StreamerRoute>
          } 
        />
        <Route path="/profile/:username" element={<ProfilePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/pro" element={<ProPage />} />
        <Route path="/ranking" element={<RankingPage />} />
        <Route path="/calendario" element={<MatchCalendarPage />} />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } 
        />
        <Route path="/s/:shortCode" element={<ShortLinkRedirect />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/streamers" element={<StreamersPage />} />
        <Route path="/config-guide" element={<ConfigGuide />} />
        <Route path="/restream" element={<StreamerRoute><RestreamSettings /></StreamerRoute>} />
        <Route 
          path="/analytics" 
          element={
            <StreamerRoute>
              <AnalyticsPage />
            </StreamerRoute>
          } 
        />
        <Route 
          path="/ads" 
          element={
            <StreamerRoute>
              <AdsPage />
            </StreamerRoute>
          } 
        />
        <Route path="/marsucoins" element={<MarsuCoinsPage />} />
        <Route path="/marsucoins/success" element={<MarsuCoinsPage />} />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      
      {/* WhatsApp Floating Button - Appears and disappears */}
      {!isTvApp && <WhatsAppButton />}
      {!isTvApp && <SupportChatWidget />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#0A0A0A',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff',
            },
          }}
        />
        <CookieBanner />
        <InstallPWA />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
